import React, { useEffect, useReducer, useRef, useState } from "react";

import { SendOutlined } from "@ant-design/icons";
import {
  Alert,
  Button,
  Card,
  Empty,
  Flex,
  Input,
  message,
  Select,
  Spin,
  Tag,
  Typography,
} from "antd";

import { ContactMatch, contactsApi } from "@/services/contacts.api";
import { getErrorMessage } from "@/utilities";

import { dolares, fecha } from "./agent-config";
import { agentApi } from "./api";
import {
  pendientes,
  pruebasReducer,
  terminada,
  textoDelEstado,
} from "./probar";

const { Text, Paragraph } = Typography;

/** Cada cuánto se vuelve a mirar una prueba sin respuesta: el pod la retira en su ciclo. */
const CADA_MS = 3000;

interface Props {
  storeCode: string;
  userId: number;
}

/**
 * «Probar» (tareas 5.5 y 5.11, D46): un mensaje, con un contacto del store opcional como contexto,
 * que el pod corre en seco con la configuración y el conocimiento de ESTE asistente —lecturas
 * reales, escrituras y envío simulados—. La respuesta llega a los pocos segundos, con las
 * herramientas que usó y lo que costó. Pide `store_operators` Admin, como guardar.
 */
export const ProbarTab: React.FC<Props> = ({ storeCode, userId }) => {
  const [pruebas, dispatch] = useReducer(pruebasReducer, []);
  const [texto, setTexto] = useState("");
  // El contacto elegido con su etiqueta: queda en las opciones aunque cambie la búsqueda.
  const [contacto, setContacto] = useState<{
    value: number;
    label: string;
  } | null>(null);
  const [q, setQ] = useState("");
  const [contactos, setContactos] = useState<ContactMatch[]>([]);
  const [buscando, setBuscando] = useState(false);
  const [enviando, setEnviando] = useState(false);
  const leyendo = useRef(false);
  // El nombre del contacto de cada prueba, para no mostrar sólo su número.
  const etiquetas = useRef(new Map<number, string>());

  // La búsqueda de contactos, como en Nuevo mensaje: siempre en el store, y sólo los que tienen ficha.
  useEffect(() => {
    if (!q.trim()) {
      setContactos([]);
      return;
    }
    let vivo = true;
    const t = setTimeout(() => {
      setBuscando(true);
      contactsApi
        .searchContacts(q, storeCode)
        .then((r) => vivo && setContactos(r.filter((c) => c.contactId)))
        .catch(() => vivo && setContactos([]))
        .finally(() => vivo && setBuscando(false));
    }, 300);
    return () => {
      vivo = false;
      clearTimeout(t);
    };
  }, [q, storeCode]);

  const abiertas = pendientes(pruebas);

  // Mientras haya pruebas sin respuesta, se vuelven a leer. Una lectura a la vez.
  useEffect(() => {
    if (abiertas.length === 0) return;
    const id = setInterval(async () => {
      if (leyendo.current) return;
      leyendo.current = true;
      try {
        for (const testId of abiertas) {
          const p = await agentApi
            .prueba(storeCode, userId, testId)
            .catch(() => null);
          if (p) dispatch({ tipo: "actualizada", prueba: p });
        }
      } finally {
        leyendo.current = false;
      }
    }, CADA_MS);
    return () => clearInterval(id);
  }, [abiertas.join(","), storeCode, userId]);

  const opciones = contactos.map((c) => ({
    value: c.contactId as number,
    label: [c.name, c.whatsapp || c.email].filter(Boolean).join(" · "),
  }));
  if (contacto && !opciones.some((o) => o.value === contacto.value))
    opciones.unshift(contacto);

  const probar = async () => {
    const input = texto.trim();
    if (!input) return;
    if (contacto) etiquetas.current.set(contacto.value, contacto.label);
    setEnviando(true);
    try {
      const p = await agentApi.probar(storeCode, userId, {
        input,
        ...(contacto ? { contactId: contacto.value } : {}),
      });
      dispatch({ tipo: "creada", prueba: p });
      setTexto("");
    } catch (e) {
      message.error(getErrorMessage(e));
    } finally {
      setEnviando(false);
    }
  };

  return (
    <Flex vertical gap={16}>
      <Paragraph type="secondary" style={{ marginBottom: 0 }}>
        Escribile como lo haría un cliente u operador. Responde con la
        configuración y el conocimiento guardados: consulta precios y stock de
        verdad, pero no crea presupuestos ni le escribe a nadie. Para probar un
        cambio del conocimiento, guardalo primero.
      </Paragraph>

      {pruebas.length === 0 ? (
        <Empty
          image={Empty.PRESENTED_IMAGE_SIMPLE}
          description="Todavía no hiciste ninguna prueba"
        />
      ) : (
        <Flex vertical gap={12}>
          {pruebas.map((p) => {
            const estado = textoDelEstado(p);
            return (
              <Card key={p.testId} size="small">
                <Flex vertical gap={8}>
                  <Flex justify="space-between" gap={8} wrap="wrap">
                    <Text strong style={{ whiteSpace: "pre-wrap" }}>
                      {p.input}
                    </Text>
                    <Text type="secondary" style={{ fontSize: 12 }}>
                      {fecha(p.createdAt)}
                      {p.context?.contactId
                        ? ` · ${
                            etiquetas.current.get(p.context.contactId) ??
                            `contacto ${p.context.contactId}`
                          }`
                        : ""}
                    </Text>
                  </Flex>
                  {!terminada(p.status) ? (
                    <Flex align="center" gap={8}>
                      <Spin size="small" />
                      <Text type="secondary">{estado}</Text>
                    </Flex>
                  ) : p.status === "done" ? (
                    <>
                      <Paragraph
                        style={{ whiteSpace: "pre-wrap", marginBottom: 0 }}
                      >
                        {p.reply || "—"}
                      </Paragraph>
                      <Flex gap={4} wrap="wrap" align="center">
                        {p.toolsCalled.length > 0 ? (
                          p.toolsCalled.map((t, i) => (
                            <Tag key={`${t}-${i}`}>{t}</Tag>
                          ))
                        ) : (
                          <Text type="secondary" style={{ fontSize: 12 }}>
                            Sin herramientas
                          </Text>
                        )}
                        {p.costEstimate !== null && (
                          <Text
                            type="secondary"
                            style={{ fontSize: 12, marginLeft: 8 }}
                          >
                            {dolares(p.costEstimate)}
                          </Text>
                        )}
                      </Flex>
                    </>
                  ) : (
                    <Alert
                      type={p.status === "failed" ? "error" : "warning"}
                      showIcon
                      message={estado}
                    />
                  )}
                </Flex>
              </Card>
            );
          })}
        </Flex>
      )}

      <Flex vertical gap={8}>
        <Select
          showSearch
          allowClear
          filterOption={false}
          value={contacto?.value}
          onSearch={setQ}
          onChange={(v) =>
            setContacto(opciones.find((o) => o.value === v) ?? null)
          }
          notFoundContent={buscando ? <Spin size="small" /> : null}
          placeholder="Un contacto del Store como contexto (opcional)"
          options={opciones}
        />
        <Input.TextArea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          maxLength={2000}
          autoSize={{ minRows: 2, maxRows: 8 }}
          placeholder="Hola, ¿cuánto sale el cemento de 50 kg?"
          onPressEnter={(e) => {
            if (!e.shiftKey) {
              e.preventDefault();
              void probar();
            }
          }}
        />
        <Flex justify="flex-end">
          <Button
            type="primary"
            icon={<SendOutlined />}
            iconPosition="end"
            loading={enviando}
            disabled={!texto.trim()}
            onClick={probar}
          >
            Probar
          </Button>
        </Flex>
      </Flex>
    </Flex>
  );
};
